/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { Box, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import { IScheduleDetailsProps } from './CreateSchedule.Model';
import {
  buttonStyles,
  ErrorMsg,
  JobNameContainer,
  JobNameWrapper,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Controller } from 'react-hook-form';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { CreateScheduleFieldNames } from '../AllJobs.Model';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { FrequencyScheduleTypes, FrequencyShiftTypes } from '../../../../../Shared/Constants/App';

const ScheduleDetails: React.FC<IScheduleDetailsProps> = ({
  t,
  control,
  selectedShiftType,
  setSelectedShiftType,
  isShiftTypeOpen,
  setShiftTypeOpen,
  totalFTE,
  setTotalFTE,
  selectedFrequency,
  setSelectedFrequency,
  isFrequencyOpen,
  setFrequencyOpen,
}) => {
  const { headingText, textField, divider } = buttonStyles;

  const handleModifyShiftType = (shiftType: string) => {
    setSelectedShiftType(shiftType);
  };

  const handleChangeFTE = (totalFTE: string) => {
    setTotalFTE(totalFTE);
  };

  const handleFrequencyChange = (value: string) => {
    setSelectedFrequency(value);
  };

  return (
    <>
      <Typography sx={headingText}>{t('jobManagement:scheduleDetails')}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}>
        <JobNameContainer>
          <Controller
            control={control}
            name={CreateScheduleFieldNames.scheduleName}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <JobNameWrapper>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('jobManagement:scheduleName')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={value}
                    onChange={(e) => {
                      if (e?.target?.value?.length <= 50) {
                        onChange(e?.target?.value);
                      }
                    }}
                  />
                  <ErrorMsg sx={{ paddingLeft: 0 }}>{error && error?.message}</ErrorMsg>
                </JobNameWrapper>
              );
            }}
          />
        </JobNameContainer>
        <Box>
          <TextField
            select
            sx={{ ...textField, marginBottom: '2rem' }}
            label={
              <span>
                {t('jobManagement:shiftType')} <span className="mandatory-field">*</span>
              </span>
            }
            value={selectedShiftType}
            onChange={(event) => handleModifyShiftType(event?.target?.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShiftTypeOpen(!isShiftTypeOpen)}
                    style={{
                      padding: 0,
                    }}>
                    <img
                      src={isShiftTypeOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                      alt={isShiftTypeOpen ? 'down' : 'up'}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            SelectProps={{
              open: isShiftTypeOpen,
              IconComponent: () => null,
              onOpen: () => setShiftTypeOpen(true),
              onClose: () => setShiftTypeOpen(false),
              MenuProps: {
                PaperProps: {
                  style: {
                    boxShadow: BoxShadow.BoxShadow,
                  },
                },
              },
            }}>
            {FrequencyShiftTypes?.map((schedule: any, index: number) => (
              <Options key={index} value={schedule?.value}>
                {schedule?.label}
              </Options>
            ))}
          </TextField>
        </Box>
        <Box>
          <JobNameContainer>
            <TextField
              sx={textField}
              id="outlined-required"
              type="number"
              inputProps={{
                min: 0,
                step: 1,
              }}
              label={
                <span>
                  {t('jobManagement:totalFTE')}
                  <span className="mandatory-field"> *</span>
                </span>
              }
              value={totalFTE}
              onChange={(event) => {
                const value = event?.target?.value;
                if (value === '' || /^\d+$/?.test(value)) {
                  handleChangeFTE(value);
                }
              }}
            />
          </JobNameContainer>
        </Box>
      </Box>
      <Divider sx={divider} />
      <Box>
        <TextField
          select
          sx={{ ...textField, marginBottom: '2rem' }}
          label={
            <span>
              {t('jobManagement:scheduleType')} <span className="mandatory-field">*</span>
            </span>
          }
          value={selectedFrequency}
          onChange={(event) => handleFrequencyChange(event.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setFrequencyOpen(!isFrequencyOpen)}
                  style={{
                    padding: 0,
                  }}>
                  <img
                    src={isFrequencyOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                    alt={isFrequencyOpen ? 'down' : 'up'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          SelectProps={{
            open: isFrequencyOpen,
            IconComponent: () => null,
            onOpen: () => setFrequencyOpen(true),
            onClose: () => setFrequencyOpen(false),
            MenuProps: {
              PaperProps: {
                style: {
                  boxShadow: BoxShadow.BoxShadow,
                },
              },
            },
          }}>
          {FrequencyScheduleTypes.map((schedule: any, index: number) => (
            <Options key={index} value={schedule?.value}>
              {schedule?.label}
            </Options>
          ))}
        </TextField>
      </Box>
    </>
  );
};

export default ScheduleDetails;
